import { LeaderGroupCombobox, UserCombobox } from '@/components'
import {
  Button as BaseButton,
  ExportButton as BaseExportButton,
  Checkbox,
  MonthPicker,
} from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import type { LeaderGroupNode } from '@/modules/companies'
import { Field } from '@/modules/companies'
import { useAdmin, usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { DateRange } from '@ur-solutions/react-components'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import type { UpdateFormFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import React, { useMemo, useState } from 'react'
import { isMobile, isMobileOnly } from 'react-device-detect'
import styled, { css } from 'styled-components'
import { DateRangePickerCustom } from '../../../components_deprecated/DatePicker/DateRangePicker/DateRangePickerCustom'
import ToggleSwitch from '../../../components_deprecated/Toggle/ToggleSwitch'
import { AbsenceView } from '../consts'
import { useAbsenceMutations } from '../mutations.hooks'
import type {
  AbsenceFilterForm,
  CreateAbsenceForm,
  TimesheetFilterForm,
} from '../types'
import { AbsencePopupFilter } from './AbsencePopupFilter'
import { CreateAbsenceModal } from './CreateAbsenceModal'

interface LeaderGroupProps {
  $leaderGroupField?: boolean
}

const Wrapper = styled(Card)<LeaderGroupProps>`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;

    & + section {
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props =>
    props.$leaderGroupField &&
    css`
      ${props.theme.media.custom({ max: '1750px' })} {
        grid-template-columns: 1fr;

        section {
          display: flex;
          flex-wrap: wrap;

          & + section {
            display: flex;
            justify-content: flex-end;
            padding-top: 0;
            border-left: 0;
          }
        }
      }
    `}
  ${props => props.theme.media.largePad} {
    grid-template-columns: 1fr;

    section {
      display: flex;
      flex-wrap: wrap;

      & + section {
        display: flex;
        justify-content: flex-end;

        padding-top: 0;
        border-left: 0;
      }
    }
  }

  ${props => props.theme.media.custom({ max: '1050px' })} {
    section + section {
      justify-content: flex-start;
    }
  }

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;

    section:first-of-type {
      display: flex;
      flex-direction: column;
      gap: 0;

      & + section {
        border-left: 0;
      }

      & > * + * {
        margin-top: 1rem;
      }
    }

    section:last-of-type {
      display: flex;
      justify-content: flex-end;
      padding-top: 0;
    }
  }
`
const Button = styled(BaseButton)<LeaderGroupProps>`
  ${props => props.theme.media.mobile} {
    padding: 1rem !important;
    text-align: center;
  }

  ${props => props.$leaderGroupField && props.theme.media.mobileAndLargePad} {
    padding: 1rem 0.4rem 1rem 2rem !important;

    span {
      display: none;
    }
  }

  ${props => props.$leaderGroupField && props.theme.media.mediumScreen} {
    padding: 1rem 0.4rem 1rem 2rem !important;
  }
`
const ExportButton = styled(BaseExportButton)`
  ${props => props.theme.media.mobile} {
    padding: 1rem !important;
    text-align: center;
  }

  ${props => props.theme.media.mobileAndLargePad} {
    padding: 1rem 0.4rem 1rem 2rem !important;

    span {
      display: none;
    }
  }
`

const SelectUsersWrapper = styled.div`
  overflow: visible;
  min-width: 260px;
  height: 54px;

  ${props => props.theme.layout.largePad} {
    width: 100%;
  }
`

const ContainerRadioButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.theme.colors.gray5};
  }
`

interface TimesheetControlsProps {
  filter: TimesheetFilterForm | AbsenceFilterForm

  type?: 'timeentries' | 'absences'
  leaderGroupsInfo?: {
    groupLoading: boolean
    leaderGroupFilter?: boolean
    leaderGroups?: LeaderGroupNode[]
  }

  onOpenExportModal?: () => void
  onUpdate: UpdateFormFn<TimesheetFilterForm> | UpdateFormFn<AbsenceFilterForm>
}

export const TimesheetControls: React.FC<TimesheetControlsProps> = ({
  filter,

  type = 'timeentries',

  leaderGroupsInfo,

  onOpenExportModal = () => void 0,
  onUpdate,
}) => {
  const translations = useTranslate({
    salaryMode: 'common.salary-mode',
    absenceType: 'timesheets.absence-type',
    month: 'common.month',
    date: 'common.date',
    user: 'common.user',
    users: 'common.users',
    export: 'common.export',
    registerHours: 'timesheets.register-timeentry',
    registerAbsence: 'absences.absence-dialog.add',
    leaderGroups: 'companies.leader-groups',
    chooseGroup: 'common.choose-group',
    dateStart: 'common.date-start',
    createDate: 'common.create-date',
  })

  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const addPrompt = usePrompt()

  const canAddTimeEntry = hasPermissions(
    PERMISSIONS.timesheets.add.timeentry,
    true
  )
  const canViewSalaryMode = hasPermissions(
    PERMISSIONS.salary.view.salaryMode,
    false
  )
  const canAddAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.absence,
    false
  )
  const canAdd = type === 'timeentries' ? canAddTimeEntry : canAddAbsence
  const showAbsenceFilter = type === 'absences'
  const showRightSection = admin || canAdd

  const canSeeOthersAbsences = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    true
  )
  const canSeeOthersTimeEntries = hasPermissions(
    PERMISSIONS.timesheets.view.otherstimeentry,
    true
  )

  const canSeeOthers =
    type === 'absences' ? canSeeOthersAbsences : canSeeOthersTimeEntries

  const {
    mutations: { create: createAbsence },
    loading: mutationsLoading,
  } = useAbsenceMutations()

  async function handleCreateAbsence() {
    const { data } = await addPrompt<CreateAbsenceForm | null>(resolve => (
      <CreateAbsenceModal admin={admin} onSubmit={resolve} />
    ))

    if (!data) return

    const userId = data.user ? data.user : filter.user

    await createAbsence({
      variables: {
        input: {
          reason: data.reason,
          absenceType: data.absenceType ?? '',
          datetimeStart: formatISO(data.dateFrom),
          datetimeEnd: formatISO(data.dateTo),
          user: userId,
        },
      },
    })
  }

  const isAbsence = type === 'absences'
  const selectedLeaderGroup = filter.leaderGroup
  const selectedLeaderGroupUsers = useMemo(
    () => new Set(filter.leaderGroupUsers ?? []),
    [filter.leaderGroupUsers]
  )

  const updateFilter = (value: string) => {
    const normalizedValue = value || null
    const selectedGroup = leaderGroupsInfo?.leaderGroups?.find(
      group => group.id === normalizedValue
    )
    const leaderGroupUsers = selectedGroup?.users.map(user => user.id) ?? []
    const shouldUnselectUser =
      !!normalizedValue && !!filter.user && !leaderGroupUsers.includes(filter.user)

    onUpdate({
      leaderGroup: normalizedValue,
      leaderGroupUsers,
      ...(shouldUnselectUser ? { user: undefined } : {}),
    })
  }

  const field = leaderGroupsInfo?.leaderGroupFilter

  const [dateRange, setDateRange] = useState<[Date, Date]>(
    filter?.date ?? [new Date(), new Date()]
  )
  const [localIsDate, setLocalIsDate] = useState<boolean>(true)
  const [localIsDateStart, setLocalIsDateStart] = useState<boolean>(
    filter.isDateStart ?? false
  )

  const handleButtonClick = async (date: {
    value: DateRange
    isDate: boolean
    isDateStart: boolean
  }) => {
    onUpdate({
      date: [date.value.from, date.value.to],
      isDate: date.isDate,
      isDateStart: date.isDateStart,
    })
  }

  return (
    <Wrapper $leaderGroupField={field}>
      <section>
        {canSeeOthers && !filter.allUsers ? (
          <Field>
            <label>
              {!filter.allUsers ? translations.user : translations.users}
            </label>

            {type === 'absences' ? (
              <SelectUsersWrapper>
                <SelectMultipleUsers
                  selected={filter?.users ?? []}
                  background="quaternary"
                  width="100%"
                  minWidth={isMobileOnly ? '0' : '260px'}
                  onChange={users => onUpdate({ users: users ?? [me.id] })}
                  filterLeaderGroup={isAbsence}
                />
              </SelectUsersWrapper>
            ) : (
              <UserCombobox
                mode="single"
                value={filter.user ?? ''}
                className={isMobileOnly ? 'w-full' : undefined}
                hideItem={item =>
                  !!selectedLeaderGroup &&
                  !selectedLeaderGroupUsers.has(item.id)
                }
                onChange={user => onUpdate({ user: user || undefined })}
              />
            )}
          </Field>
        ) : (
          <></>
        )}

        {leaderGroupsInfo?.leaderGroupFilter && (
          <Field>
            <label>{translations.leaderGroups}</label>
            <LeaderGroupCombobox
              mode="single"
              value={selectedLeaderGroup ?? ''}
              module="Timesheets"
              clearable
              fullWidth
              placeholder={translations.chooseGroup}
              onChange={updateFilter}
            />
          </Field>
        )}

        {showAbsenceFilter && (
          <Field>
            <label>{translations.absenceType}</label>
            <AbsencePopupFilter
              filter={filter as AbsenceFilterForm}
              admin={admin}
              onUpdate={onUpdate as UpdateFormFn<AbsenceFilterForm>}
            />
          </Field>
        )}

        {filter.view === AbsenceView.table &&
        filter.date &&
        filter.isDate !== undefined &&
        filter.isDateStart !== undefined ? (
          <Field>
            <DateRangePickerCustom
              label={translations.date}
              value={{ from: dateRange[0], to: dateRange[1] }}
              onChange={date => setDateRange([date.from, date.to])}
              isDateStart={localIsDateStart}
              isDate={localIsDate}
              onToggleDate={(val, type) => {
                if (type === 'isDate') {
                  setLocalIsDate(val)
                } else {
                  setLocalIsDateStart(val)
                }
              }}
              onButtonClick={handleButtonClick}
            />
          </Field>
        ) : (
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={filter.month}
              disableAfter={type === 'absences' ? false : true}
              minWidth={isMobileOnly ? '0' : '340px'}
              width={isMobileOnly ? '100%' : undefined}
              maxWidth={isMobileOnly ? '100%' : '420px'}
              onChange={month => onUpdate({ month })}
            />
          </Field>
        )}

        {canViewSalaryMode && (
          <Field>
            {isMobile ? (
              <ContainerRadioButton>
                <label>{translations.salaryMode}</label>
                <ToggleSwitch
                  value={filter.salaryMode}
                  onChange={checked => onUpdate({ salaryMode: checked })}
                />
              </ContainerRadioButton>
            ) : (
              <>
                <label>{translations.salaryMode}</label>
                <Checkbox
                  checked={filter.salaryMode}
                  onChange={checked => onUpdate({ salaryMode: checked })}
                  boxSize="3.875rem"
                  padding="2rem"
                  boxBorderRadius="8px"
                />
              </>
            )}
          </Field>
        )}
      </section>

      {showRightSection && (
        <section>
          {type !== 'absences' && admin && (
            <ExportButton
              iconLeftProps={{ icon: 'download' }}
              fullWidth={isMobileOnly}
              onClick={onOpenExportModal}
            >
              {/* TODO not correct icon */}
              {isMobileOnly ? (
                translations.export
              ) : (
                <span>{translations.export}</span>
              )}
            </ExportButton>
          )}

          {canAdd && (
            <>
              {type === 'timeentries' ? (
                <PlainLink
                  to="/time-entries/register"
                  state={{ initialUser: filter.user }}
                >
                  {isMobileOnly ? (
                    <Button
                      padding="0 1rem !important"
                      $leaderGroupField={field}
                    >
                      <Icon icon="plus" margin="0" />
                    </Button>
                  ) : (
                    <Button
                      iconLeftProps={{ icon: 'plus' }}
                      $leaderGroupField={field}
                    >
                      <span>{translations.registerHours}</span>
                    </Button>
                  )}
                </PlainLink>
              ) : (
                <>
                  {/* This is irrelevant and will be removed */}
                  {isMobileOnly ? (
                    <Button
                      padding="0 1rem !important"
                      onClick={handleCreateAbsence}
                      disabled={mutationsLoading}
                      fullWidth
                      $leaderGroupField={field}
                    >
                      <Icon icon="plus" margin="0 1rem 0 0" />
                      {translations.registerAbsence}
                    </Button>
                  ) : (
                    <>
                      {/* <AbsenceDialog canAddForOthers={canAddOthersAbsence} /> */}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </section>
      )}
    </Wrapper>
  )
}
